<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import AiAgentsAPI from 'dashboard/api/aiAgents';
import AiAgentPromptTemplatesAPI from 'dashboard/api/aiAgents/promptTemplates';
import AiAgentKnowledgePanel from './AiAgentKnowledgePanel.vue';
import AiAgentMentionTextArea from './AiAgentMentionTextArea.vue';
import {
  AI_AGENT_ACTIONS,
  MODEL_OPTIONS,
  PROVIDER_OPTIONS,
  TEMPERATURE_OPTIONS,
  VARIABLES,
} from './constants';

const props = defineProps({
  agent: {
    type: Object,
    default: () => ({}),
  },
  isSaving: {
    type: Boolean,
    default: false,
  },
  isPublishing: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    required: true,
  },
  showRunsLink: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['save', 'publish', 'cancel', 'viewRuns']);
const { t } = useI18n();
const store = useStore();

const accountAgents = useMapGetter('agents/getVerifiedAgents');
const accountTeams = useMapGetter('teams/getTeams');
const lifecycleStages = useMapGetter('lifecycleStages/getLifecycleStages');

const defaultInstructions = t('AI_AGENTS.FORM.INSTRUCTIONS_PLACEHOLDER');

const form = reactive({
  emoji: '🤖',
  name: '',
  description: '',
  showDescription: false,
  instructions: defaultInstructions,
  provider: 'openai',
  model: 'gpt-4o-mini',
  temperature: 0.3,
  handoffMessage: '',
  resolutionMessage: '',
  guardrailsText: '',
  guidelinesText: '',
});

const actionState = reactive(
  AI_AGENT_ACTIONS.reduce((acc, action) => {
    acc[action.type] = {
      isEnabled: false,
      instructions: '',
      config: {},
    };
    return acc;
  }, {})
);

const activeTestTab = ref('chat');
const testMessage = ref('');
const testMessages = ref([]);
const isTesting = ref(false);

const mockContact = reactive({
  lifecycle_stage_id: '',
  assignee_key: 'unassigned',
  first_name: 'John',
  last_name: 'Doe',
  phone_country_code: '+20',
  phone_number: '+201000000000',
  email: 'test@example.com',
  country: '',
  language: '',
  tags: [],
});

const testConversationState = reactive({
  assignedToHuman: false,
});

const lifecycleOptions = computed(() =>
  (lifecycleStages.value || []).map(stage => ({
    value: String(stage.id),
    label: stage.name,
  }))
);

const assigneeOptions = computed(() => {
  const options = [
    {
      value: 'unassigned',
      label: t('AI_AGENTS.TEST_PANEL.CONTACT_FIELD_OPTIONS.UNASSIGNED'),
      assignee_type: null,
      assignee_id: null,
    },
  ];

  (accountAgents.value || []).forEach(agent => {
    options.push({
      value: `user:${agent.id}`,
      label: agent.name || agent.available_name,
      assignee_type: 'User',
      assignee_id: agent.id,
    });
  });

  (accountTeams.value || []).forEach(team => {
    options.push({
      value: `team:${team.id}`,
      label: team.name,
      assignee_type: 'Team',
      assignee_id: team.id,
    });
  });

  return options;
});

const selectedAssignee = computed(() =>
  assigneeOptions.value.find(
    option => option.value === mockContact.assignee_key
  )
);

const phoneCountryCodeOptions = computed(() => [
  {
    value: '+20',
    label: t('AI_AGENTS.TEST_PANEL.CONTACT_FIELD_OPTIONS.EGYPT_PHONE_CODE'),
  },
  {
    value: '+966',
    label: t('AI_AGENTS.TEST_PANEL.CONTACT_FIELD_OPTIONS.SAUDI_PHONE_CODE'),
  },
  {
    value: '+971',
    label: t('AI_AGENTS.TEST_PANEL.CONTACT_FIELD_OPTIONS.UAE_PHONE_CODE'),
  },
  {
    value: '+1',
    label: t('AI_AGENTS.TEST_PANEL.CONTACT_FIELD_OPTIONS.US_PHONE_CODE'),
  },
]);

const countryOptions = computed(() => [
  {
    value: 'Egypt',
    label: t('AI_AGENTS.TEST_PANEL.CONTACT_FIELD_OPTIONS.EGYPT'),
  },
  {
    value: 'Saudi Arabia',
    label: t('AI_AGENTS.TEST_PANEL.CONTACT_FIELD_OPTIONS.SAUDI_ARABIA'),
  },
  {
    value: 'United Arab Emirates',
    label: t('AI_AGENTS.TEST_PANEL.CONTACT_FIELD_OPTIONS.UAE'),
  },
]);

const languageOptions = computed(() => [
  {
    value: 'Arabic',
    label: t('AI_AGENTS.TEST_PANEL.CONTACT_FIELD_OPTIONS.ARABIC'),
  },
  {
    value: 'English',
    label: t('AI_AGENTS.TEST_PANEL.CONTACT_FIELD_OPTIONS.ENGLISH'),
  },
]);

const canSave = computed(
  () => form.name.trim().length > 0 && form.instructions.trim().length > 0
);

const providerLabels = computed(() => ({
  openai: t('AI_AGENTS.PROVIDERS.OPENAI'),
}));

const providerOptions = computed(() =>
  PROVIDER_OPTIONS.map(provider => ({
    ...provider,
    label: providerLabels.value[provider.value],
  }))
);

const filteredModelOptions = computed(() =>
  MODEL_OPTIONS.filter(model => model.provider === form.provider)
);

const temperatureLabels = computed(() => ({
  0.2: t('AI_AGENTS.TEMPERATURES.PRECISE'),
  0.3: t('AI_AGENTS.TEMPERATURES.BALANCED'),
  0.7: t('AI_AGENTS.TEMPERATURES.CREATIVE'),
}));

const temperatureOptions = computed(() =>
  TEMPERATURE_OPTIONS.map(option => ({
    ...option,
    label: temperatureLabels.value[option.value],
  }))
);

const promptTemplates = ref([]);

const actionLabels = computed(() => ({
  close_conversation: {
    title: t('AI_AGENTS.ACTION_CARDS.CLOSE_CONVERSATION.TITLE'),
    description: t('AI_AGENTS.ACTION_CARDS.CLOSE_CONVERSATION.DESCRIPTION'),
    instruction: t('AI_AGENTS.ACTION_CARDS.CLOSE_CONVERSATION.INSTRUCTION'),
    placeholder: t('AI_AGENTS.ACTION_CARDS.CLOSE_CONVERSATION.PLACEHOLDER'),
  },
  assign_agent: {
    title: t('AI_AGENTS.ACTION_CARDS.ASSIGN.TITLE'),
    description: t('AI_AGENTS.ACTION_CARDS.ASSIGN.DESCRIPTION'),
    instruction: t('AI_AGENTS.ACTION_CARDS.ASSIGN.INSTRUCTION'),
    placeholder: t('AI_AGENTS.ACTION_CARDS.ASSIGN.PLACEHOLDER'),
  },
  update_lifecycle_stage: {
    title: t('AI_AGENTS.ACTION_CARDS.LIFECYCLE.TITLE'),
    description: t('AI_AGENTS.ACTION_CARDS.LIFECYCLE.DESCRIPTION'),
    instruction: t('AI_AGENTS.ACTION_CARDS.LIFECYCLE.INSTRUCTION'),
    placeholder: t('AI_AGENTS.ACTION_CARDS.LIFECYCLE.PLACEHOLDER'),
  },
  update_contact_fields: {
    title: t('AI_AGENTS.ACTION_CARDS.CONTACT_FIELDS.TITLE'),
    description: t('AI_AGENTS.ACTION_CARDS.CONTACT_FIELDS.DESCRIPTION'),
    instruction: t('AI_AGENTS.ACTION_CARDS.CONTACT_FIELDS.INSTRUCTION'),
    placeholder: t('AI_AGENTS.ACTION_CARDS.CONTACT_FIELDS.PLACEHOLDER'),
  },
  update_tags: {
    title: t('AI_AGENTS.ACTION_CARDS.TAGS.TITLE'),
    description: t('AI_AGENTS.ACTION_CARDS.TAGS.DESCRIPTION'),
    instruction: t('AI_AGENTS.ACTION_CARDS.TAGS.INSTRUCTION'),
    placeholder: t('AI_AGENTS.ACTION_CARDS.TAGS.PLACEHOLDER'),
  },
  trigger_workflow: {
    title: t('AI_AGENTS.ACTION_CARDS.WORKFLOWS.TITLE'),
    description: t('AI_AGENTS.ACTION_CARDS.WORKFLOWS.DESCRIPTION'),
    instruction: t('AI_AGENTS.ACTION_CARDS.WORKFLOWS.INSTRUCTION'),
    placeholder: t('AI_AGENTS.ACTION_CARDS.WORKFLOWS.PLACEHOLDER'),
  },
  add_comment: {
    title: t('AI_AGENTS.ACTION_CARDS.COMMENTS.TITLE'),
    description: t('AI_AGENTS.ACTION_CARDS.COMMENTS.DESCRIPTION'),
    instruction: t('AI_AGENTS.ACTION_CARDS.COMMENTS.INSTRUCTION'),
    placeholder: t('AI_AGENTS.ACTION_CARDS.COMMENTS.PLACEHOLDER'),
  },
  handle_calls: {
    title: t('AI_AGENTS.ACTION_CARDS.CALLS.TITLE'),
    description: t('AI_AGENTS.ACTION_CARDS.CALLS.DESCRIPTION'),
  },
  make_http_request: {
    title: t('AI_AGENTS.ACTION_CARDS.HTTP.TITLE'),
    description: t('AI_AGENTS.ACTION_CARDS.HTTP.DESCRIPTION'),
  },
}));

const configuredActions = computed(() =>
  AI_AGENT_ACTIONS.map(action => ({
    ...action,
    ...(actionLabels.value[action.type] || {}),
    state: actionState[action.type],
  }))
);

const actionPayload = computed(() => [
  ...configuredActions.value.map(action => ({
    action_type: action.type,
    is_enabled: action.state.isEnabled,
    instructions: action.state.instructions,
    config: action.state.config || {},
  })),
  ...['assign_team', 'handoff'].map(actionType => ({
    action_type: actionType,
    is_enabled: false,
    instructions: '',
    config: {},
  })),
]);

const payload = computed(() => ({
  emoji: form.emoji,
  name: form.name.trim(),
  description: form.showDescription ? form.description : '',
  instructions: form.instructions,
  provider: form.provider,
  model: form.model,
  temperature: Number(form.temperature),
  handoff_message: form.handoffMessage,
  resolution_message: form.resolutionMessage,
  guardrails: form.guardrailsText
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean),
  response_guidelines: form.guidelinesText
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean),
  actions: actionPayload.value,
}));

const agentId = computed(() => props.agent?.id);

const mockContactPayload = computed(() => {
  const lifecycleStage = lifecycleOptions.value.find(
    option => option.value === String(mockContact.lifecycle_stage_id)
  );
  const assignee = selectedAssignee.value;

  return {
    name: `${mockContact.first_name} ${mockContact.last_name}`.trim(),
    first_name: mockContact.first_name,
    last_name: mockContact.last_name,
    email: mockContact.email,
    phone_number: mockContact.phone_number,
    lifecycle_stage: lifecycleStage?.label || '',
    lifecycle_stage_id: mockContact.lifecycle_stage_id || null,
    assignee:
      assignee?.label ||
      t('AI_AGENTS.TEST_PANEL.CONTACT_FIELD_OPTIONS.UNASSIGNED'),
    assignee_type: assignee?.assignee_type || null,
    assignee_id: assignee?.assignee_id || null,
    assignee_name: assignee?.label || null,
    country: mockContact.country,
    language: mockContact.language,
    tags: mockContact.tags,
  };
});

const formatExecutedAction = action => {
  const assignee = action?.result?.assignee;
  if (assignee?.assignee_name) {
    return `${action.type}: ${assignee.assignee_name}`;
  }
  return action.type;
};

const applyMockContactFromResponse = mockContactResponse => {
  if (!mockContactResponse) return;

  if (mockContactResponse.lifecycle_stage_id) {
    mockContact.lifecycle_stage_id = String(
      mockContactResponse.lifecycle_stage_id
    );
  }

  if (['User', 'Team'].includes(mockContactResponse.assignee_type)) {
    testConversationState.assignedToHuman = true;
    mockContact.assignee_key =
      mockContactResponse.assignee_type === 'User'
        ? `user:${mockContactResponse.assignee_id}`
        : `team:${mockContactResponse.assignee_id}`;
  }
};

const applyTestActionsExecuted = actionsExecuted => {
  (actionsExecuted || []).forEach(action => {
    const assignee = action?.result?.assignee;
    if (!assignee) return;

    if (['User', 'Team'].includes(assignee.assignee_type)) {
      testConversationState.assignedToHuman = true;
      mockContact.assignee_key =
        assignee.assignee_type === 'User'
          ? `user:${assignee.assignee_id}`
          : `team:${assignee.assignee_id}`;
    } else if (assignee.handed_off) {
      testConversationState.assignedToHuman = true;
      mockContact.assignee_key = 'unassigned';
    }
  });
};

const syncActions = agentActions => {
  Object.values(actionState).forEach(state => {
    state.isEnabled = false;
    state.instructions = '';
    state.config = {};
  });

  const legacyAssignmentInstructions = [];
  let legacyAssignmentEnabled = false;

  (agentActions || []).forEach(action => {
    if (['assign_team', 'handoff'].includes(action.action_type)) {
      legacyAssignmentEnabled ||= Boolean(action.is_enabled);
      if (action.is_enabled && action.instructions) {
        legacyAssignmentInstructions.push(action.instructions);
      }
      return;
    }

    const state = actionState[action.action_type];
    if (!state) return;

    state.isEnabled = Boolean(action.is_enabled);
    state.instructions = action.instructions || '';
    state.config = action.config || {};
  });

  if (legacyAssignmentEnabled) {
    actionState.assign_agent.isEnabled = true;
    actionState.assign_agent.instructions = [
      actionState.assign_agent.instructions,
      ...legacyAssignmentInstructions,
    ]
      .filter(Boolean)
      .join('\n');
  }
};

const syncForm = agent => {
  form.emoji = agent.emoji || '🤖';
  form.name = agent.name || '';
  form.description = agent.description || '';
  form.showDescription = Boolean(agent.description);
  form.instructions = agent.instructions || defaultInstructions;
  form.provider = agent.provider || 'openai';
  form.model = agent.model || 'gpt-4o-mini';
  form.temperature = Number(agent.temperature ?? 0.3);
  form.handoffMessage = agent.handoff_message || '';
  form.resolutionMessage = agent.resolution_message || '';
  form.guardrailsText = (agent.guardrails || []).join('\n');
  form.guidelinesText = (agent.response_guidelines || []).join('\n');
  syncActions(agent.actions);
};

const insertVariable = variable => {
  form.instructions = `${form.instructions}\n{{${variable}}}`;
};

const applyTemplate = event => {
  const template = promptTemplates.value.find(
    item => String(item.id) === event.target.value
  );
  if (!template) return;

  form.instructions = template.prompt;
  event.target.value = '';
};

const sendTestMessage = async () => {
  const content = testMessage.value.trim();
  if (!content || !canSave.value || isTesting.value) return;

  if (testConversationState.assignedToHuman) {
    useAlert(t('AI_AGENTS.TEST_PANEL.ASSIGNED_STOP'));
    return;
  }

  const userMessage = {
    id: `user-${Date.now()}`,
    role: 'user',
    content,
  };
  testMessages.value.push(userMessage);
  testMessage.value = '';

  try {
    isTesting.value = true;
    const response = await AiAgentsAPI.test(
      {
        ai_agent: payload.value,
        messages: testMessages.value.map(({ role, content: message }) => ({
          role,
          content: message,
        })),
        mock_contact: mockContactPayload.value,
      },
      props.agent?.id
    );
    applyTestActionsExecuted(response.data.actions_executed);
    applyMockContactFromResponse(response.data.mock_contact);

    const reply =
      response.data.reply ||
      (testConversationState.assignedToHuman
        ? t('AI_AGENTS.TEST_PANEL.ASSIGNED_REPLY_STOP')
        : t('AI_AGENTS.TEST_PANEL.EMPTY_REPLY'));

    testMessages.value.push({
      id: `assistant-${Date.now()}`,
      role: 'assistant',
      content: reply,
      actions: (response.data.actions_executed || []).map(formatExecutedAction),
    });
  } catch (error) {
    const errorMessage =
      error?.response?.data?.error || t('AI_AGENTS.TEST_PANEL.ERROR');
    useAlert(errorMessage);
    testMessages.value.push({
      id: `assistant-error-${Date.now()}`,
      role: 'assistant',
      content: errorMessage,
    });
  } finally {
    isTesting.value = false;
  }
};

const resetTestChat = () => {
  testMessages.value = [];
  testConversationState.assignedToHuman = false;
  mockContact.assignee_key = 'unassigned';
};

const handleSave = () => emit('save', payload.value);
const handlePublish = () => emit('publish', payload.value);

const loadPromptTemplates = async () => {
  try {
    const { data } = await AiAgentPromptTemplatesAPI.get();
    promptTemplates.value = data || [];
  } catch {
    promptTemplates.value = [];
  }
};

onMounted(() => {
  store.dispatch('agents/get');
  store.dispatch('teams/get');
  store.dispatch('lifecycleStages/get');
  loadPromptTemplates();
});

watch(
  () => props.agent,
  agent => syncForm(agent || {}),
  { immediate: true }
);

watch(
  () => form.provider,
  () => {
    const currentModel = filteredModelOptions.value.find(
      model => model.value === form.model
    );
    if (!currentModel && filteredModelOptions.value.length) {
      form.model = filteredModelOptions.value[0].value;
    }
  }
);
</script>

<template>
  <div
    class="flex h-full min-h-0 w-full min-w-0 flex-1 flex-col overflow-hidden bg-n-surface-1"
  >
    <header
      class="z-10 flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-n-weak bg-n-background px-4 py-3 md:px-8"
    >
      <div class="flex items-center min-w-0 gap-3">
        <Button
          icon="i-lucide-arrow-left"
          slate
          ghost
          size="sm"
          :title="t('AI_AGENTS.ACTIONS.BACK')"
          @click="emit('cancel')"
        />
        <h1 class="m-0 text-lg font-semibold truncate text-n-slate-12">
          {{ title }}
        </h1>
      </div>
      <div class="flex items-center gap-2">
        <Button
          v-if="showRunsLink && agentId"
          :label="t('AI_AGENTS.ACTIONS.VIEW_RUNS')"
          slate
          faded
          size="sm"
          @click="emit('viewRuns')"
        />
        <Button
          :label="t('AI_AGENTS.ACTIONS.CANCEL')"
          slate
          faded
          size="sm"
          @click="emit('cancel')"
        />
        <Button
          :label="t('AI_AGENTS.ACTIONS.SAVE_DRAFT')"
          slate
          size="sm"
          :disabled="!canSave"
          :is-loading="isSaving"
          @click="handleSave"
        />
        <Button
          :label="t('AI_AGENTS.ACTIONS.PUBLISH')"
          size="sm"
          :disabled="!canSave"
          :is-loading="isPublishing"
          @click="handlePublish"
        />
      </div>
    </header>

    <div class="min-h-0 flex-1 overflow-x-hidden overflow-y-auto">
      <div
        class="mx-auto grid w-full min-w-0 max-w-[1600px] items-start gap-6 p-4 md:gap-8 md:p-8 xl:grid-cols-[minmax(0,1fr)_22rem] 2xl:grid-cols-[minmax(0,1fr)_26rem]"
      >
        <section class="grid min-w-0 gap-8">
          <article class="grid min-w-0 gap-5">
            <div>
              <h2 class="m-0 text-base font-semibold text-n-slate-12">
                {{ t('AI_AGENTS.FORM.CONFIGURATION') }}
              </h2>
              <p class="m-0 mt-1 text-sm text-n-slate-11">
                {{ t('AI_AGENTS.FORM.CONFIGURATION_DESCRIPTION') }}
              </p>
            </div>

            <div class="grid gap-4 md:grid-cols-[4rem_minmax(0,1fr)]">
              <Input
                v-model="form.emoji"
                :label="t('AI_AGENTS.FORM.EMOJI')"
                custom-input-class="text-center text-lg"
              />
              <Input
                v-model="form.name"
                :label="t('AI_AGENTS.FORM.NAME')"
                :placeholder="t('AI_AGENTS.FORM.NAME_PLACEHOLDER')"
              />
            </div>

            <label
              class="inline-flex items-center gap-2 text-sm text-n-slate-11"
            >
              <input
                v-model="form.showDescription"
                type="checkbox"
                class="m-0 rounded border-n-weak text-n-brand focus:ring-n-brand"
              />
              <span>{{ t('AI_AGENTS.FORM.SHOW_DESCRIPTION') }}</span>
            </label>

            <TextArea
              v-if="form.showDescription"
              v-model="form.description"
              :label="t('AI_AGENTS.FORM.DESCRIPTION')"
              :placeholder="t('AI_AGENTS.FORM.DESCRIPTION_PLACEHOLDER')"
              auto-height
              min-height="4rem"
              max-height="8rem"
            />

            <div
              class="grid gap-4 p-4 border rounded-lg border-n-weak bg-n-solid-2"
            >
              <div>
                <h3 class="m-0 text-sm font-semibold text-n-slate-12">
                  {{ t('AI_AGENTS.FORM.INSTRUCTIONS') }}
                </h3>
                <p class="m-0 mt-1 text-sm text-n-slate-11">
                  {{ t('AI_AGENTS.FORM.INSTRUCTIONS_DESCRIPTION') }}
                </p>
              </div>

              <TextArea
                v-model="form.instructions"
                :placeholder="t('AI_AGENTS.FORM.INSTRUCTIONS_PLACEHOLDER')"
                auto-height
                resize
                min-height="8rem"
                max-height="24rem"
              />

              <div class="flex flex-wrap items-center justify-between gap-3">
                <div class="flex flex-wrap gap-2">
                  <Button
                    v-for="variable in VARIABLES"
                    :key="variable"
                    :label="`{{${variable}}}`"
                    slate
                    faded
                    size="xs"
                    @click="insertVariable(variable)"
                  />
                </div>
                <div class="flex flex-wrap items-center gap-2">
                  <select
                    class="h-8 px-3 text-sm border rounded-lg outline-none border-n-weak bg-n-alpha-black2 text-n-slate-12 focus:border-n-brand"
                    @change="applyTemplate"
                  >
                    <option value="">
                      {{ t('AI_AGENTS.FORM.PROMPT_TEMPLATES') }}
                    </option>
                    <option
                      v-for="template in promptTemplates"
                      :key="template.id"
                      :value="template.id"
                    >
                      {{ template.title }}
                    </option>
                  </select>
                  <Button
                    :label="t('AI_AGENTS.FORM.OPTIMIZE')"
                    icon="i-lucide-wand-sparkles"
                    slate
                    faded
                    size="sm"
                    disabled
                    :title="t('AI_AGENTS.FORM.OPTIMIZE_DISABLED')"
                  />
                </div>
              </div>

              <Button
                :label="t('AI_AGENTS.FORM.LEARN_HOW')"
                icon="i-lucide-book-open"
                link
                size="xs"
                disabled
              />
            </div>

            <div class="grid gap-4 md:grid-cols-3">
              <label class="grid gap-1">
                <span class="text-xs font-medium text-n-slate-11">
                  {{ t('AI_AGENTS.FORM.PROVIDER') }}
                </span>
                <select
                  v-model="form.provider"
                  class="h-10 px-3 text-sm border rounded-lg outline-none border-n-weak bg-n-alpha-black2 text-n-slate-12 focus:border-n-brand"
                >
                  <option
                    v-for="provider in providerOptions"
                    :key="provider.value"
                    :value="provider.value"
                  >
                    {{ provider.label }}
                  </option>
                </select>
              </label>
              <label class="grid gap-1">
                <span class="text-xs font-medium text-n-slate-11">
                  {{ t('AI_AGENTS.FORM.MODEL') }}
                </span>
                <select
                  v-model="form.model"
                  class="h-10 px-3 text-sm border rounded-lg outline-none border-n-weak bg-n-alpha-black2 text-n-slate-12 focus:border-n-brand"
                >
                  <option
                    v-for="model in filteredModelOptions"
                    :key="model.value"
                    :value="model.value"
                  >
                    {{ model.label }}
                  </option>
                </select>
              </label>
              <label class="grid gap-1">
                <span class="text-xs font-medium text-n-slate-11">
                  {{ t('AI_AGENTS.FORM.TEMPERATURE') }}
                </span>
                <select
                  v-model="form.temperature"
                  class="h-10 px-3 text-sm border rounded-lg outline-none border-n-weak bg-n-alpha-black2 text-n-slate-12 focus:border-n-brand"
                >
                  <option
                    v-for="temperature in temperatureOptions"
                    :key="temperature.value"
                    :value="temperature.value"
                  >
                    {{ temperature.label }}
                  </option>
                </select>
              </label>
            </div>
          </article>

          <article class="grid min-w-0 gap-5">
            <div>
              <h2 class="m-0 text-base font-semibold text-n-slate-12">
                {{ t('AI_AGENTS.ACTIONS_SECTION.TITLE') }}
              </h2>
              <p class="max-w-4xl m-0 mt-1 text-sm text-n-slate-11">
                {{ t('AI_AGENTS.ACTIONS_SECTION.DESCRIPTION') }}
              </p>
            </div>

            <div class="grid gap-4">
              <section
                v-for="action in configuredActions"
                :key="action.type"
                class="grid gap-4 p-4 border rounded-lg border-n-weak bg-n-solid-2"
              >
                <div class="flex items-start justify-between gap-4">
                  <div class="min-w-0">
                    <div class="flex flex-wrap items-center gap-2">
                      <h3 class="m-0 text-sm font-semibold text-n-slate-12">
                        {{ action.title }}
                      </h3>
                      <span
                        v-if="action.locked"
                        class="px-2 py-0.5 text-xs font-medium rounded-md bg-n-alpha-2 text-n-slate-11"
                      >
                        {{ t('AI_AGENTS.ACTIONS_SECTION.COMING_SOON') }}
                      </span>
                    </div>
                    <p class="m-0 mt-1 text-sm text-n-slate-11">
                      {{ action.description }}
                    </p>
                  </div>
                  <button
                    v-if="action.locked"
                    type="button"
                    disabled
                    class="relative flex-shrink-0 w-8 h-5 rounded-full cursor-not-allowed bg-n-slate-6"
                  >
                    <span
                      class="absolute w-4 h-4 rounded-full top-0.5 ltr:left-0.5 rtl:right-0.5 bg-n-background"
                    />
                  </button>
                  <Switch v-else v-model="action.state.isEnabled" />
                </div>

                <div
                  v-if="action.state.isEnabled && !action.locked"
                  class="grid gap-3"
                >
                  <AiAgentMentionTextArea
                    v-if="action.type === 'assign_agent'"
                    v-model="action.state.instructions"
                    :label="action.instruction"
                    :placeholder="action.placeholder"
                    enable-workspace
                    enable-teams
                    enable-agents
                  />
                  <TextArea
                    v-else
                    v-model="action.state.instructions"
                    :label="action.instruction"
                    :placeholder="action.placeholder"
                    auto-height
                    min-height="4rem"
                    max-height="10rem"
                  />
                  <div
                    class="flex flex-wrap items-center justify-between gap-3"
                  >
                    <Button
                      :label="t('AI_AGENTS.FORM.LEARN_HOW')"
                      icon="i-lucide-book-open"
                      link
                      size="xs"
                      disabled
                    />
                    <Button
                      :label="t('AI_AGENTS.FORM.OPTIMIZE')"
                      icon="i-lucide-wand-sparkles"
                      slate
                      faded
                      size="sm"
                      disabled
                    />
                  </div>
                </div>
              </section>
            </div>
          </article>

          <article class="grid min-w-0 gap-5 pb-4">
            <h2 class="m-0 text-base font-semibold text-n-slate-12">
              {{ t('AI_AGENTS.GUARDRAILS.TITLE') }}
            </h2>
            <div
              class="grid gap-4 p-4 border rounded-lg border-n-weak bg-n-solid-2"
            >
              <TextArea
                v-model="form.guardrailsText"
                :label="t('AI_AGENTS.GUARDRAILS.DESCRIPTION')"
                :placeholder="t('AI_AGENTS.GUARDRAILS.PLACEHOLDER')"
                rows="3"
              />
              <TextArea
                v-model="form.guidelinesText"
                :label="t('AI_AGENTS.GUARDRAILS.GUIDELINES_TITLE')"
                :placeholder="t('AI_AGENTS.GUARDRAILS.PLACEHOLDER')"
                rows="3"
              />
              <Input
                v-model="form.handoffMessage"
                :label="t('AI_AGENTS.GUARDRAILS.HANDOFF_MESSAGE')"
              />
              <Input
                v-model="form.resolutionMessage"
                :label="t('AI_AGENTS.GUARDRAILS.RESOLUTION_MESSAGE')"
              />
            </div>
          </article>

          <article class="grid min-w-0 gap-5 pb-4">
            <h2 class="m-0 text-base font-semibold text-n-slate-12">
              {{ t('AI_AGENTS.KNOWLEDGE.TITLE') }}
            </h2>
            <div
              class="grid gap-4 p-4 border rounded-lg border-n-weak bg-n-solid-2"
            >
              <AiAgentKnowledgePanel v-if="agentId" :agent-id="agentId" />
              <p v-else class="m-0 text-sm text-n-slate-11">
                {{ t('AI_AGENTS.KNOWLEDGE.SAVE_AGENT_FIRST') }}
              </p>
            </div>
          </article>
        </section>

        <aside
          class="flex min-h-[28rem] min-w-0 flex-col overflow-hidden rounded-lg border border-n-weak bg-n-solid-2 xl:sticky xl:top-6 xl:max-h-[calc(100vh-5.5rem)] xl:self-start"
        >
          <div class="flex min-h-0 flex-1 flex-col">
            <div class="flex items-center justify-between gap-3 p-4">
              <h2 class="m-0 text-base font-semibold text-n-slate-12">
                {{ t('AI_AGENTS.TEST_PANEL.TITLE') }}
              </h2>
              <Button
                :label="t('AI_AGENTS.TEST_PANEL.RESET_CHAT')"
                icon="i-lucide-phone-call"
                slate
                ghost
                size="sm"
                :disabled="testMessages.length === 0"
                @click="resetTestChat"
              />
            </div>
            <div class="flex gap-4 px-4 border-b border-n-weak">
              <button
                type="button"
                class="px-1 pb-3 text-sm font-medium border-b-2"
                :class="
                  activeTestTab === 'chat'
                    ? 'text-n-brand border-n-brand'
                    : 'text-n-slate-11 border-transparent'
                "
                @click="activeTestTab = 'chat'"
              >
                {{ t('AI_AGENTS.TEST_PANEL.CHAT_TAB') }}
              </button>
              <button
                type="button"
                class="px-1 pb-3 text-sm font-medium border-b-2"
                :class="
                  activeTestTab === 'contact_fields'
                    ? 'text-n-brand border-n-brand'
                    : 'text-n-slate-11 border-transparent'
                "
                @click="activeTestTab = 'contact_fields'"
              >
                {{ t('AI_AGENTS.TEST_PANEL.CONTACT_FIELDS_TAB') }}
              </button>
            </div>

            <div
              v-if="activeTestTab === 'chat'"
              class="flex flex-col min-h-0 grow"
            >
              <div
                v-if="testConversationState.assignedToHuman"
                class="px-4 pt-4"
              >
                <p
                  class="m-0 rounded-lg border border-n-weak bg-n-alpha-2 px-3 py-2 text-sm text-n-slate-11"
                >
                  {{ t('AI_AGENTS.TEST_PANEL.ASSIGNED_BANNER') }}
                </p>
              </div>
              <div
                class="grid content-center flex-1 min-h-0 gap-4 p-4 overflow-y-auto"
              >
                <div
                  v-if="testMessages.length === 0"
                  class="grid justify-items-center gap-2 text-center"
                >
                  <h3 class="m-0 text-base font-semibold text-n-slate-12">
                    {{ t('AI_AGENTS.TEST_PANEL.EMPTY_TITLE') }}
                  </h3>
                  <p class="max-w-sm m-0 text-sm text-n-slate-11">
                    {{ t('AI_AGENTS.TEST_PANEL.EMPTY_DESCRIPTION') }}
                  </p>
                </div>
                <div v-else class="grid content-start gap-3">
                  <div
                    v-for="message in testMessages"
                    :key="message.id"
                    class="grid gap-2"
                    :class="message.role === 'user' ? 'justify-items-end' : ''"
                  >
                    <p
                      class="max-w-[80%] px-3 py-2 m-0 text-sm rounded-lg"
                      :class="
                        message.role === 'user'
                          ? 'bg-n-brand text-white'
                          : 'bg-n-alpha-2 text-n-slate-12'
                      "
                    >
                      {{ message.content }}
                    </p>
                    <div
                      v-if="message.actions?.length"
                      class="flex flex-wrap gap-2 max-w-[80%]"
                    >
                      <span
                        v-for="action in message.actions"
                        :key="action"
                        class="px-2 py-1 text-xs rounded-md bg-n-alpha-2 text-n-slate-11"
                      >
                        {{ action }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <form
                class="grid gap-3 p-4 border-t border-n-weak"
                @submit.prevent="sendTestMessage"
              >
                <div
                  class="grid gap-2 p-3 border rounded-lg border-n-weak bg-n-alpha-black2"
                >
                  <textarea
                    v-model="testMessage"
                    rows="3"
                    class="w-full p-0 text-sm bg-transparent border-0 outline-none resize-none text-n-slate-12 placeholder:text-n-slate-10"
                    :placeholder="
                      testConversationState.assignedToHuman
                        ? t('AI_AGENTS.TEST_PANEL.ASSIGNED_PLACEHOLDER')
                        : t('AI_AGENTS.TEST_PANEL.MESSAGE_PLACEHOLDER')
                    "
                    :disabled="
                      !canSave || testConversationState.assignedToHuman
                    "
                  />
                  <div class="flex items-center justify-between gap-2">
                    <div class="flex gap-2 text-n-slate-10">
                      <span class="i-lucide-smile" />
                      <span class="i-lucide-at-sign" />
                    </div>
                    <Button
                      icon="i-lucide-send-horizontal"
                      size="sm"
                      :disabled="
                        !canSave ||
                        !testMessage.trim() ||
                        isTesting ||
                        testConversationState.assignedToHuman
                      "
                      :is-loading="isTesting"
                      type="submit"
                    />
                  </div>
                </div>
                <p class="m-0 text-xs text-center text-n-slate-10">
                  {{ t('AI_AGENTS.TEST_PANEL.FOOTER') }}
                </p>
              </form>
            </div>

            <div v-else class="flex flex-col min-h-0 p-4 overflow-y-auto">
              <div class="grid gap-4 pb-6">
                <label class="grid gap-1">
                  <span class="text-sm font-medium text-n-slate-11">
                    {{ t('AI_AGENTS.TEST_PANEL.CONTACT_FIELDS.LIFECYCLE') }}
                  </span>
                  <select
                    v-model="mockContact.lifecycle_stage_id"
                    class="h-10 px-0 text-sm bg-transparent border-0 outline-none text-n-slate-12"
                  >
                    <option value="">
                      {{
                        t('AI_AGENTS.TEST_PANEL.CONTACT_FIELDS.ADD_LIFECYCLE')
                      }}
                    </option>
                    <option
                      v-for="option in lifecycleOptions"
                      :key="option.value"
                      :value="option.value"
                    >
                      {{ option.label }}
                    </option>
                  </select>
                </label>

                <label class="grid gap-1">
                  <span class="text-sm font-medium text-n-slate-11">
                    {{ t('AI_AGENTS.TEST_PANEL.CONTACT_FIELDS.ASSIGNEE') }}
                  </span>
                  <select
                    v-model="mockContact.assignee_key"
                    class="h-10 px-0 text-sm bg-transparent border-0 outline-none text-n-slate-12"
                  >
                    <option
                      v-for="option in assigneeOptions"
                      :key="option.value"
                      :value="option.value"
                    >
                      {{ option.label }}
                    </option>
                  </select>
                </label>

                <label class="grid gap-1">
                  <span class="text-sm font-medium text-n-slate-11">
                    {{ t('AI_AGENTS.TEST_PANEL.CONTACT_FIELDS.FIRST_NAME') }}
                  </span>
                  <input
                    v-model="mockContact.first_name"
                    class="h-8 px-0 text-sm bg-transparent border-0 outline-none text-n-slate-12 placeholder:text-n-slate-10"
                    :placeholder="
                      t('AI_AGENTS.TEST_PANEL.CONTACT_FIELDS.ADD_FIRST_NAME')
                    "
                  />
                </label>

                <label class="grid gap-1">
                  <span class="text-sm font-medium text-n-slate-11">
                    {{ t('AI_AGENTS.TEST_PANEL.CONTACT_FIELDS.LAST_NAME') }}
                  </span>
                  <input
                    v-model="mockContact.last_name"
                    class="h-8 px-0 text-sm bg-transparent border-0 outline-none text-n-slate-12 placeholder:text-n-slate-10"
                    :placeholder="
                      t('AI_AGENTS.TEST_PANEL.CONTACT_FIELDS.ADD_LAST_NAME')
                    "
                  />
                </label>

                <div class="grid gap-1">
                  <span class="text-sm font-medium text-n-slate-11">
                    {{ t('AI_AGENTS.TEST_PANEL.CONTACT_FIELDS.PHONE') }}
                  </span>
                  <div class="grid grid-cols-[4rem_minmax(0,1fr)] gap-2">
                    <select
                      v-model="mockContact.phone_country_code"
                      class="h-8 px-0 text-sm bg-transparent border-0 outline-none text-n-slate-12"
                    >
                      <option
                        v-for="option in phoneCountryCodeOptions"
                        :key="option.value"
                        :value="option.value"
                      >
                        {{ option.label }}
                      </option>
                    </select>
                    <input
                      v-model="mockContact.phone_number"
                      class="h-8 px-0 text-sm bg-transparent border-0 outline-none text-n-slate-12 placeholder:text-n-slate-10"
                      :placeholder="
                        t('AI_AGENTS.TEST_PANEL.CONTACT_FIELDS.ADD_PHONE')
                      "
                    />
                  </div>
                </div>

                <label class="grid gap-1">
                  <span class="text-sm font-medium text-n-slate-11">
                    {{ t('AI_AGENTS.TEST_PANEL.CONTACT_FIELDS.EMAIL') }}
                  </span>
                  <input
                    v-model="mockContact.email"
                    type="email"
                    class="h-8 px-0 text-sm bg-transparent border-0 outline-none text-n-slate-12 placeholder:text-n-slate-10"
                    :placeholder="
                      t('AI_AGENTS.TEST_PANEL.CONTACT_FIELDS.ADD_EMAIL')
                    "
                  />
                </label>

                <label class="grid gap-1">
                  <span class="text-sm font-medium text-n-slate-11">
                    {{ t('AI_AGENTS.TEST_PANEL.CONTACT_FIELDS.COUNTRY') }}
                  </span>
                  <select
                    v-model="mockContact.country"
                    class="h-10 px-0 text-sm bg-transparent border-0 outline-none text-n-slate-12"
                  >
                    <option value="">
                      {{ t('AI_AGENTS.TEST_PANEL.CONTACT_FIELDS.ADD_COUNTRY') }}
                    </option>
                    <option
                      v-for="option in countryOptions"
                      :key="option.value"
                      :value="option.value"
                    >
                      {{ option.label }}
                    </option>
                  </select>
                </label>

                <label class="grid gap-1">
                  <span class="text-sm font-medium text-n-slate-11">
                    {{ t('AI_AGENTS.TEST_PANEL.CONTACT_FIELDS.LANGUAGE') }}
                  </span>
                  <select
                    v-model="mockContact.language"
                    class="h-10 px-0 text-sm bg-transparent border-0 outline-none text-n-slate-12"
                  >
                    <option value="">
                      {{
                        t('AI_AGENTS.TEST_PANEL.CONTACT_FIELDS.ADD_LANGUAGE')
                      }}
                    </option>
                    <option
                      v-for="option in languageOptions"
                      :key="option.value"
                      :value="option.value"
                    >
                      {{ option.label }}
                    </option>
                  </select>
                </label>
              </div>

              <div class="pt-3 mt-auto border-t border-n-weak">
                <div class="flex items-center justify-between gap-3">
                  <span class="text-sm font-semibold text-n-slate-12">
                    {{ t('AI_AGENTS.TEST_PANEL.CONTACT_FIELDS.TAGS') }}
                  </span>
                  <Button icon="i-lucide-plus" slate faded size="xs" disabled />
                </div>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  </div>
</template>
